/**
 * Static layout data for the Analytics Dashboard (matches product mock).
 * Wire to APIs here when backend metrics are available.
 */
export function useAnalyticsDashboardLayout() {
    const filterOptions = {
        projects: [
            { value: 'all', label: 'All projects' },
            { value: 'p1', label: 'Mobile App' },
            { value: 'p2', label: 'Web Platform' },
        ],
        teams: [
            { value: 'all', label: 'All teams' },
            { value: 'eng', label: 'Engineering' },
            { value: 'design', label: 'Design' },
        ],
        dateRanges: [
            { value: '30d', label: 'Last 30 days' },
            { value: '7d', label: 'Last 7 days' },
            { value: '90d', label: 'Last 90 days' },
        ],
    };

    const kpiCards = [
        { key: '1', label: 'Active Projects', value: '24', trend: 12, trendUp: true, icon: 'folder' },
        { key: '2', label: 'Completed Tasks', value: '87', trend: 8, trendUp: true, icon: 'check' },
        { key: '3', label: 'Total Tasks', value: '1247', trend: 5, trendUp: true, icon: 'list' },
        { key: '4', label: 'Team Members', value: '42', trend: 0, trendUp: true, icon: 'users' },
        { key: '5', label: 'Open Bugs', value: '18', trend: -3, trendUp: false, icon: 'bug' },
        { key: '6', label: 'Sprints Active', value: '6', trend: 2, trendUp: true, icon: 'zap' },
        { key: '7', label: 'Milestones Due', value: '9', trend: -1, trendUp: false, icon: 'flag' },
        { key: '8', label: 'Avg. Velocity', value: '34', trend: 4, trendUp: true, icon: 'gauge' },
        { key: '9', label: 'On-Time Rate', value: '78%', trend: 6, trendUp: true, icon: 'clock' },
        { key: '10', label: 'Client Satisfaction', value: '4.6', trend: 2, trendUp: true, icon: 'star' },
    ];

    const projectStatusDonut = [
        { name: 'Completed', value: 45, color: '#22c55e' },
        { name: 'Active', value: 38, color: '#3b82f6' },
        { name: 'At Risk', value: 12, color: '#eab308' },
        { name: 'Blocked', value: 5, color: '#ef4444' },
    ];

    const projectProgress = [
        { name: 'Mobile App', percent: 92, status: 'ok' },
        { name: 'Web Platform', percent: 74, status: 'warn' },
        { name: 'API Gateway', percent: 58, status: 'warn' },
        { name: 'Data Pipeline', percent: 41, status: 'bad' },
        { name: 'Design System', percent: 88, status: 'ok' },
    ];

    const deliveryPerformance = {
        onTimePercent: 78,
        delayPercent: 22,
        avgDelayDays: 2.2,
    };

    const milestoneStatusBars = [
        { name: 'Not Started', value: 12 },
        { name: 'In Progress', value: 28 },
        { name: 'Review', value: 9 },
        { name: 'Done', value: 34 },
    ];

    const readinessMilestone = [
        { name: 'Jan', value: 62 },
        { name: 'Feb', value: 71 },
        { name: 'Mar', value: 68 },
        { name: 'Apr', value: 79 },
        { name: 'May', value: 85 },
    ];

    const taskCompletionLine = [
        { name: 'Week 1', completed: 42, total: 58 },
        { name: 'Week 2', completed: 51, total: 62 },
        { name: 'Week 3', completed: 48, total: 65 },
        { name: 'Week 4', completed: 63, total: 70 },
    ];

    const sprintVelocity = [
        { name: 'S1', value: 28 },
        { name: 'S2', value: 32 },
        { name: 'S3', value: 30 },
        { name: 'S4', value: 38 },
        { name: 'S5', value: 35 },
    ];

    const taskStatusDonut = [
        { name: 'To Do', value: 42, color: '#94a3b8' },
        { name: 'In Progress', value: 35, color: '#3b82f6' },
        { name: 'Review', value: 18, color: '#eab308' },
        { name: 'Done', value: 124, color: '#22c55e' },
    ];

    const bugTrends = [
        { name: 'Mon', reported: 8, resolved: 5 },
        { name: 'Tue', reported: 12, resolved: 9 },
        { name: 'Wed', reported: 6, resolved: 8 },
        { name: 'Thu', reported: 10, resolved: 11 },
        { name: 'Fri', reported: 7, resolved: 9 },
    ];

    const bugSeverityDonut = [
        { name: 'Critical', value: 6, color: '#ef4444' },
        { name: 'High', value: 14, color: '#f97316' },
        { name: 'Medium', value: 28, color: '#eab308' },
        { name: 'Low', value: 22, color: '#22c55e' },
    ];

    const qaMetrics = [
        { key: 'avg', label: 'Avg Resolution Time', value: '2.4 days', tone: 'blue' },
        { key: 'reopen', label: 'Bugs Reopened', value: '12', tone: 'yellow' },
        { key: 'fix', label: 'Successful Fixes', value: '32', tone: 'green' },
    ];

    const teamWorkload = [
        { name: 'Alex', value: 42 },
        { name: 'Sam', value: 38 },
        { name: 'Jordan', value: 35 },
        { name: 'Taylor', value: 29 },
        { name: 'Casey', value: 33 },
    ];

    const topContributors = [
        { rank: 1, name: 'Alex Morgan', role: 'Lead Developer', score: 98 },
        { rank: 2, name: 'Sam Rivera', role: 'Full Stack', score: 94 },
        { rank: 3, name: 'Jordan Lee', role: 'QA Engineer', score: 91 },
        { rank: 4, name: 'Taylor Kim', role: 'Designer', score: 87 },
    ];

    const workModeDonut = [
        { name: 'Remote', value: 45, color: '#3b82f6' },
        { name: 'Office', value: 32, color: '#22c55e' },
        { name: 'Hybrid', value: 23, color: '#a855f7' },
    ];

    const workHoursLine = [
        { name: 'Mon', hours: 7.2 },
        { name: 'Tue', hours: 8.1 },
        { name: 'Wed', hours: 7.8 },
        { name: 'Thu', hours: 8.4 },
        { name: 'Fri', hours: 7.5 },
    ];

    const timeByProject = [
        { name: 'Mobile', hours: 42 },
        { name: 'Web', hours: 36 },
        { name: 'API', hours: 28 },
        { name: 'Data', hours: 22 },
    ];

    const recentActivity = [
        { id: '1', text: "New project 'Mobile App' created", time: '2 hours ago', tone: 'blue' },
        { id: '2', text: 'Sprint 12 marked complete', time: '5 hours ago', tone: 'green' },
        { id: '3', text: 'Milestone "Beta release" delayed', time: 'Yesterday', tone: 'yellow' },
        { id: '4', text: '12 bugs resolved in QA sweep', time: 'Yesterday', tone: 'green' },
        { id: '5', text: 'Client feedback incorporated', time: '2 days ago', tone: 'blue' },
    ];

    return {
        filterOptions,
        kpiCards,
        projectStatusDonut,
        projectProgress,
        deliveryPerformance,
        milestoneStatusBars,
        readinessMilestone,
        taskCompletionLine,
        sprintVelocity,
        taskStatusDonut,
        bugTrends,
        bugSeverityDonut,
        qaMetrics,
        teamWorkload,
        topContributors,
        workModeDonut,
        workHoursLine,
        timeByProject,
        recentActivity,
    };
}
